import { Controller } from "@hotwired/stimulus"

/**
 * Saves an inline QR as a PNG, so a public link can go on a poster or a
 * handout without anyone screenshotting a sheet.
 *
 * The QR on the page is an SVG rendered server-side (`shared/_qr_code`), which
 * a browser will not download as an image on its own. This serializes it,
 * paints it onto a canvas at a print-usable size, and hands over the result.
 *
 * The canvas is drawn from a `data:` URL rather than a blob: an SVG loaded
 * cross-origin taints the canvas and `toDataURL` then throws. A data URL is
 * same-origin by definition, so the export always succeeds.
 *
 * An optional `overlay` target is composited onto the middle at the proportions
 * the page shows it, so the file is the artwork on screen rather than a bare
 * code that happens to encode the same URL.
 *
 *   <div data-controller="qr-download" data-qr-download-filename-value="link.png">
 *     <div data-qr-download-target="source"><svg …></div>
 *     <div data-qr-download-target="overlay"><svg …logo…></div>
 *     <button data-action="click->qr-download#download">Download PNG</button>
 *   </div>
 */
const EXPORT_SIZE = 1024
// Share of the code's width the brand mark and its white chip take, matching
// `_general_access`: an 11-unit mark on a 52-unit code, inside p-2 of padding.
const MARK_SCALE = 0.21
const CHIP_SCALE = 0.29

export default class extends Controller<HTMLElement> {
  static targets = ["source", "overlay"]
  static values = {
    filename: { type: String, default: "qr-code.png" },
    size: { type: Number, default: EXPORT_SIZE },
  }

  declare readonly hasSourceTarget: boolean
  declare readonly sourceTarget: HTMLElement
  declare readonly hasOverlayTarget: boolean
  declare readonly overlayTarget: HTMLElement
  declare filenameValue: string
  declare sizeValue: number

  async download() {
    const code = await this.rasterize(this.svgIn(this.hasSourceTarget && this.sourceTarget))
    if (!code) return

    const mark = await this.rasterize(this.svgIn(this.hasOverlayTarget && this.overlayTarget))
    this.paint(code, mark)
  }

  private svgIn(element: HTMLElement | false | null): SVGElement | null {
    return element ? element.querySelector("svg") : null
  }

  // A data URL rather than a blob: an SVG loaded cross-origin taints the canvas
  // and `toDataURL` then throws. Same-origin by definition, so this cannot.
  private rasterize(svg: SVGElement | null): Promise<HTMLImageElement | null> {
    if (!svg) return Promise.resolve(null)

    return new Promise((resolve) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => resolve(null)
      image.src = `data:image/svg+xml;base64,${b64(new XMLSerializer().serializeToString(svg))}`
    })
  }

  private paint(code: HTMLImageElement, mark: HTMLImageElement | null) {
    const canvas = document.createElement("canvas")
    canvas.width = this.sizeValue
    canvas.height = this.sizeValue

    const context = canvas.getContext("2d")
    if (!context) return

    // The QR's quiet zone is part of the symbol, but the surrounding page is
    // what supplies its whiteness — on a canvas it would export transparent,
    // and a scanner needs the contrast.
    context.fillStyle = "#ffffff"
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(code, 0, 0, canvas.width, canvas.height)
    if (mark) this.paintMark(context, mark)

    const link = document.createElement("a")
    link.href = canvas.toDataURL("image/png")
    link.download = this.filenameValue
    link.click()
  }

  // The white chip is what makes the mark legible against the modules behind
  // it, and it is the occlusion level :h was chosen to absorb.
  private paintMark(context: CanvasRenderingContext2D, mark: HTMLImageElement) {
    const middle = this.sizeValue / 2
    const chip = this.sizeValue * CHIP_SCALE
    const glyph = this.sizeValue * MARK_SCALE

    context.fillStyle = "#ffffff"
    context.beginPath()
    context.roundRect(middle - chip / 2, middle - chip / 2, chip, chip, chip * 0.22)
    context.fill()
    context.drawImage(mark, middle - glyph / 2, middle - glyph / 2, glyph, glyph)
  }
}

// btoa only takes latin-1; an SVG can carry anything.
function b64(markup: string): string {
  return btoa(String.fromCharCode(...new TextEncoder().encode(markup)))
}
